import { useCallback, useEffect, useRef, useState } from 'react';
import AdminPanel from '../components/AdminPanel';
import MovieLibrary from '../components/MovieLibrary';
import NowPlaying from '../components/NowPlaying';
import ProfilePanel from '../components/ProfilePanel';
import SyncPlayer from '../components/SyncPlayer';
import UpNext from '../components/UpNext';
import WatchedList from '../components/WatchedList';
import YouTubeRequest from '../components/YouTubeRequest';
import BrandLogo from '../components/BrandLogo';
import IdleScreen from '../components/theater/IdleScreen';
import ViewerVolume from '../components/theater/ViewerVolume';
import VirtualTheater from '../components/theater/VirtualTheater';
import { useSocket } from '../hooks/useSocket';
import {
  api,
  getHouseCode,
  queuePrivateHouse,
  setHouseCode,
  takeQueuedPrivateHouse,
  type TheaterPreferences,
  type User,
} from '../lib/api';
import './Dashboard.css';

const DEFAULT_PREFS: TheaterPreferences = {
  theater_characters: true,
  thrown_effects: true,
  speech_bubbles: true,
  reactions: true,
};
const BANNER_MS = 3000;

export default function Dashboard({
  user,
  watchOnly = false,
  onSignIn,
  onLogout,
}: {
  user: User | null;
  watchOnly?: boolean;
  onSignIn?: () => void;
  onLogout?: () => Promise<void>;
}) {
  const [houseCode, setHouseCodeState] = useState<string | null>(() => getHouseCode());
  const [codeDraft, setCodeDraft] = useState('');
  const [houseBusy, setHouseBusy] = useState(false);
  const isAdmin = !watchOnly && user?.role === 'ADMIN';
  const {
    playback,
    setPlayback,
    queue,
    setQueue,
    history,
    setHistory,
    correction,
    clearCorrection,
    syncError,
    clearSyncError,
    sendHeartbeat,
    sendReadyStatus,
    clockOffset,
    occupants,
    theaterCount,
    inTheater,
    joinError,
    clearJoinError,
    chatMessages,
    reactions,
    throws,
    rareAnnouncement,
    theaterNotice,
    throwables,
    setMyUserId,
    joinTheater,
    leaveTheater,
    sendChat,
    sendThrow,
    sendReaction,
    connected,
  } = useSocket(true, houseCode);

  const house = playback?.house;
  const canHost =
    !watchOnly &&
    Boolean(user) &&
    (user?.role === 'ADMIN' ||
      (house?.kind === 'private' && house.hostUserId === user?.id));
  const isGuest = user?.role === 'GUEST';
  const triedJoin = useRef(false);

  useEffect(() => {
    triedJoin.current = false;
  }, [houseCode]);

  const [adminOpen, setAdminOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<TheaterPreferences>(DEFAULT_PREFS);

  useEffect(() => {
    if (watchOnly || !user) return;
    setMyUserId(user.id);
  }, [user, watchOnly, setMyUserId]);

  useEffect(() => {
    if (watchOnly || !connected || inTheater || triedJoin.current) return;
    triedJoin.current = true;
    joinTheater();
  }, [watchOnly, connected, inTheater, joinTheater]);

  useEffect(() => {
    if (watchOnly) return;
    void api
      .theaterMe()
      .then((res) => setPreferences(res.preferences))
      .catch(() => undefined);
  }, [watchOnly]);

  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(() => setError(null), BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!syncError) return;
    const timer = window.setTimeout(() => clearSyncError(), BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [syncError, clearSyncError]);

  useEffect(() => {
    if (!joinError) return;
    const timer = window.setTimeout(() => clearJoinError(), BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [joinError, clearJoinError]);

  const refreshQueue = useCallback(async () => {
    const res = await api.queue();
    setQueue(res.items);
  }, [setQueue]);

  const refreshHistory = useCallback(async () => {
    if (watchOnly) return;
    const res = await api.history();
    setHistory(res.items);
  }, [watchOnly, setHistory]);

  const refreshPlayback = useCallback(async () => {
    const res = await api.playback();
    setPlayback(res);
  }, [setPlayback]);

  useEffect(() => {
    void Promise.all([refreshQueue(), refreshPlayback(), refreshHistory()]).catch((err) =>
      setError((err as Error).message),
    );
  }, [houseCode, refreshQueue, refreshPlayback, refreshHistory]);

  useEffect(() => {
    const onQueue = () => {
      void refreshQueue();
      void refreshPlayback();
      void refreshHistory();
    };
    window.addEventListener('queue:updated', onQueue);
    return () => {
      window.removeEventListener('queue:updated', onQueue);
    };
  }, [refreshQueue, refreshPlayback, refreshHistory]);

  const playingId = playback?.movie?.id ?? null;
  useEffect(() => {
    void refreshQueue();
  }, [playingId, refreshQueue]);

  async function flagItem(id: string, flagged: boolean) {
    setError(null);
    try {
      await api.flagQueueItem(id, flagged);
      await Promise.all([refreshQueue(), refreshHistory(), refreshPlayback()]);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function requestVideo(youtubeUrl: string) {
    setError(null);
    setNotice(null);
    const res = await api.addToQueue(youtubeUrl);
    await Promise.all([refreshQueue(), refreshPlayback()]);
    if (res.started) {
      setNotice(`${res.item.movie.title} is coming up after a few ads`);
    } else {
      setNotice(`Added ${res.item.movie.title} to the watch queue`);
    }
  }

  const headerAlert = error || syncError;
  const headerNotice = headerAlert ? null : notice;
  const intermissionNotice =
    playback?.phase === 'break'
      ? playback.next_movie
        ? `UP NEXT · ${playback.next_movie.title}`
        : 'That YouTube video has ended.'
      : null;
  const privateCode =
    house?.kind === 'private' ? house.code : house?.kind === 'public' ? null : houseCode;

  function applyHouse(code: string | null) {
    setHouseCode(code);
    setHouseCodeState(code);
    triedJoin.current = false;
    setCodeDraft('');
  }

  async function joinByCode(raw: string) {
    const code = raw.trim().toUpperCase();
    if (!code) {
      applyHouse(null);
      return;
    }
    setHouseBusy(true);
    setError(null);
    try {
      const res = await api.joinHouse(code);
      applyHouse(res.house.code);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setHouseBusy(false);
    }
  }

  async function startPrivateHouse() {
    setHouseBusy(true);
    setError(null);
    try {
      const res = await api.createPrivateHouse();
      applyHouse(res.house.code);
      setNotice(`Private house code ${res.house.code}. Share it with your group.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setHouseBusy(false);
    }
  }

  useEffect(() => {
    if (watchOnly || !user) return;
    if (!takeQueuedPrivateHouse()) return;
    void startPrivateHouse();
    // Open a queued private house once after sign-in from the join card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchOnly, user]);

  return (
    <div className="app-shell dashboard">
      <header className="topbar container">
        <div className="brand">
          <BrandLogo />
        </div>
        <div className="topbar-house">
          <div className="topbar-av">
            <div className="topbar-people">
              {theaterCount} {theaterCount === 1 ? 'PERSON' : 'PEOPLE'}
            </div>
            <ViewerVolume />
          </div>
          <form
            className="topbar-code"
            onSubmit={(e) => {
              e.preventDefault();
              void joinByCode(codeDraft);
            }}
          >
            <div className="topbar-code-label">Private Theatre</div>
            <div className="topbar-code-row">
              {privateCode ? (
                <span className="topbar-code-value" title="Share this code">
                  {privateCode}
                </span>
              ) : (
                <input
                  className="topbar-code-input"
                  value={codeDraft}
                  maxLength={4}
                  spellCheck={false}
                  autoCapitalize="characters"
                  placeholder="CODE"
                  aria-label="Private house join code"
                  onChange={(e) => setCodeDraft(e.target.value.toUpperCase())}
                  disabled={houseBusy}
                />
              )}
              {privateCode ? (
                <>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => {
                      if (privateCode) void navigator.clipboard?.writeText(privateCode);
                    }}
                  >
                    Copy
                  </button>
                  <button
                    className="btn"
                    type="button"
                    disabled={houseBusy}
                    onClick={() => applyHouse(null)}
                  >
                    Back to Public Theatre
                  </button>
                </>
              ) : (
                <>
                  <button className="btn" type="submit" disabled={houseBusy || codeDraft.length !== 4}>
                    Join
                  </button>
                  <button
                    className="btn"
                    type="button"
                    disabled={houseBusy}
                    onClick={() => {
                      if (watchOnly) {
                        queuePrivateHouse();
                        onSignIn?.();
                        return;
                      }
                      void startPrivateHouse();
                    }}
                  >
                    Create
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
        <div className="topbar-status" aria-live="polite">
          {headerAlert && (
            <div className="error-banner topbar-banner" title={headerAlert}>
              {headerAlert}
            </div>
          )}
          {joinError && (
            <div className="error-banner topbar-banner" title={joinError}>
              {joinError}
            </div>
          )}
          {headerNotice && (
            <div className="notice-banner topbar-banner" title={headerNotice}>
              {headerNotice}
            </div>
          )}
          {theaterNotice && (
            <div className="notice-banner topbar-banner" title={theaterNotice}>
              {theaterNotice}
            </div>
          )}
          {intermissionNotice && (
            <div className="notice-banner topbar-banner" title={intermissionNotice}>
              {intermissionNotice}
            </div>
          )}
        </div>
        <div className="user-chip">
          {watchOnly ? (
            <button className="btn btn-gold" onClick={() => onSignIn?.()}>
              Sign in to sit down
            </button>
          ) : (
            <>
              <span className="avatar" aria-hidden>
                👤
              </span>
              <span>{user?.display_name || user?.username}</span>
              <button className="btn" onClick={() => setProfileOpen(true)}>
                Profile
              </button>
              {isAdmin && (
                <button className="btn" onClick={() => setAdminOpen(true)}>
                  Admin
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => void onLogout?.()}>
                {isGuest ? 'Leave' : 'Logout'}
              </button>
            </>
          )}
        </div>
      </header>

      <main className="container main">
        <VirtualTheater
          userId={user?.id ?? ''}
          watchOnly={watchOnly}
          onSignIn={onSignIn}
          preferences={preferences}
          occupants={occupants}
          inTheater={watchOnly ? false : inTheater}
          joinError={joinError}
          chatMessages={chatMessages}
          reactions={reactions}
          throws={throws}
          rareAnnouncement={rareAnnouncement}
          throwables={throwables}
          notice={null}
          onJoin={() => {
            if (watchOnly) {
              onSignIn?.();
              return;
            }
            joinTheater();
          }}
          onLeave={leaveTheater}
          onChat={sendChat}
          onThrow={(objectType, x, y) => {
            sendThrow(objectType, x, y);
          }}
          onReaction={sendReaction}
          screenSlot={
            playback?.movie ? (
              <SyncPlayer
                key={playback.movie.id}
                playback={playback}
                isAdmin={canHost}
                correction={correction}
                onClearCorrection={clearCorrection}
                onHeartbeat={watchOnly ? () => undefined : sendHeartbeat}
                onReadyStatus={watchOnly ? () => undefined : sendReadyStatus}
                clockOffset={clockOffset}
                onStartAnyway={() => void api.startAnyway().then(setPlayback)}
                onSkip={() => void api.skip().then(setPlayback).then(refreshQueue)}
                onRemoveRequest={() => void api.stop().then(setPlayback).then(refreshQueue)}
                onJoinedWatching={() => {
                  if (watchOnly || inTheater) return;
                  joinTheater();
                }}
                onLocalEnded={(info) => {
                  if (watchOnly) return;
                  const movieId = playback.movie?.id;
                  void (async () => {
                    try {
                      const next = await api.ended(movieId, info);
                      setPlayback(next);
                      await refreshQueue();
                    } catch (err) {
                      setError((err as Error).message);
                    }
                  })();
                }}
              />
            ) : (
              <IdleScreen playback={playback} clockOffset={clockOffset} />
            )
          }
          belowScreen={null}
        />

        <div className={`dashboard-columns${watchOnly ? ' is-watch-only' : ''}`}>
          <NowPlaying
            playback={playback}
            theaterCount={theaterCount}
            isAdmin={canHost}
            onPause={() => void api.pause().then(setPlayback)}
            onResume={() => void api.resume().then(setPlayback)}
            onRestart={() => void api.restart().then(setPlayback)}
            onSkip={() => void api.skip().then(setPlayback).then(refreshQueue).then(refreshHistory)}
            onStop={() => void api.stop().then(setPlayback).then(refreshQueue).then(refreshHistory)}
            onSeek={(seconds) => void api.seek(seconds).then(setPlayback)}
            onStartAnyway={() => void api.startAnyway().then(setPlayback)}
            username={user?.username ?? ''}
            onFlag={watchOnly ? undefined : flagItem}
            watchOnly={watchOnly}
          />
          {!watchOnly && (
            <UpNext
              items={queue}
              isAdmin={canHost}
              username={user?.username ?? ''}
              hasNowPlaying={Boolean(playback?.movie)}
              onRemove={(id) => void api.removeQueue(id).then(refreshQueue)}
              onPlay={(movieId) => void api.play(movieId).then(setPlayback).then(refreshQueue)}
              onClear={() => void api.clearQueue().then(refreshQueue)}
              onFlag={flagItem}
            />
          )}
        </div>

        {!watchOnly && (
          <>
            <YouTubeRequest onRequest={requestVideo} />

            <MovieLibrary
              queuedVideoIds={queue.map((item) => item.movie.youtube_video_id)}
              nowPlayingVideoId={playback?.movie?.youtube_video_id}
              onRequest={requestVideo}
            />

            <WatchedList
              items={history}
              isAdmin={canHost}
              username={user?.username ?? ''}
              queuedVideoIds={queue.map((item) => item.movie.youtube_video_id)}
              nowPlayingVideoId={playback?.movie?.youtube_video_id}
              onRemove={(id) =>
                void api
                  .removePlayed(id)
                  .then(refreshHistory)
                  .catch((err) => setError((err as Error).message))
              }
              onFlag={flagItem}
              onQueue={(url) =>
                requestVideo(url).catch((err) => {
                  setError((err as Error).message);
                })
              }
            />
          </>
        )}
      </main>

      {!watchOnly && (
        <>
          <AdminPanel
            open={adminOpen}
            onClose={() => setAdminOpen(false)}
            theaterOccupants={occupants.map((o) => ({
              userId: o.userId,
              username: o.username,
              displayName: o.displayName,
            }))}
          />
          <ProfilePanel
            open={profileOpen}
            onClose={() => setProfileOpen(false)}
            onUpdated={(_c, prefs) => setPreferences(prefs)}
          />
        </>
      )}
    </div>
  );
}
